/**
 * The door to the `legal-apps` chunk: the Integrations page's legal apps view (legal apps delivery 2,
 * 2026-09-29) and the legal apps card's rows in chat (delivery 3).
 *
 * The Integrations page rides the first download and the chat engine follows it, and the legal catalog is 63 KB,
 * so the view, the card's rows and the catalog are fetched only when someone opens the "legal apps" chip (or
 * arrives at /integrations?category=legal), searches the Integrations page (2026-10-08: a search finds every legal
 * app too, fetched as the search box is focused), or when a legal apps card is on screen in a thread — never by a
 * visitor who does none of these. It holds nothing but the one dynamic import; the download is loaded here and nowhere
 * else (scripts/check-chat-first-paint.mjs, R4).
 */
import { useEffect, useState } from 'react';
import { createOnDemandModule } from '../../../utils/onDemandModule';
// A TYPE-only import: erased at build, never an edge into the chunk.
import type * as LegalAppsSurfaceNamespace from './legalAppsSurface';

export type LegalAppsSectionModule = typeof LegalAppsSurfaceNamespace;

const view = createOnDemandModule<LegalAppsSectionModule>(
  () => import(/* webpackChunkName: "legal-apps" */ './legalAppsSurface'),
);

/**
 * The view once it has arrived, or null, and whether its download failed (after its retries).
 * @param wanted the "legal apps" chip is open, or the page is being searched, so fetch now.
 * The failure is asked of the same single download the module uses; a failed one is forgotten, so opening
 * the chip again tries again.
 */
export function useLegalAppsSection(wanted: boolean): { module: LegalAppsSectionModule | null; failed: boolean } {
  const module = view.useModule(wanted, wanted);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!wanted || module) {
      setFailed(false);
      return undefined;
    }
    let alive = true;
    view.load().then(
      () => undefined,
      () => {
        if (alive) setFailed(true);
      },
    );
    return () => {
      alive = false;
    };
  }, [wanted, module]);
  return { module, failed };
}

/**
 * The download for the chat's legal apps card (delivery 3): fetched at once by a message that is one, and null
 * until it arrives (the card's stand-in shows meanwhile, and if it never does).
 */
export function useLegalAppsTileModule(wanted: boolean): LegalAppsSectionModule | null {
  return view.useModule(wanted, wanted);
}

/**
 * The download itself, for a press that must not decide before it arrives: Enter in the Integrations search waits for
 * the legal catalog, so a legal app's exact name never goes down the browser lane. A failed one is forgotten.
 */
export const loadLegalAppsSection = (): Promise<LegalAppsSectionModule> => view.load();

/** Tests only: forget the download. */
export const resetLegalAppsSectionForTests = view.resetForTests;
